/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Asset, Ducks, EntryPoint, MenuUiArea, MenuItem, Configs, EpgUtils } from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import { Modal, Rail as RailComponent, Spinner } from 'ui/components';
import Constants from 'configs/Constants';
import MainSection from 'types/MainSection';
import { useLocation } from 'react-router-dom';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import useIsOnViewportObserver from 'hooks/useIsOnViewportObserver';
import SearchBar from './SearchBar';
import * as SearchDuck from '../../../store/ducks/search';
import * as Gdpr from '../../../core/store/ducks/gdpr';
import translate from '../../../services/i18n/translate';
import SearchFilterList from './SearchFiltersList';
import OptInRail from '../recommendations/OptInRail';
import { ESearchFilter, ISearchFilter } from '../../../types/components/Search';
import Grid from '../Grid';
import { useSearchAnalytics } from '../../../hooks/useAnalytics';
import { useRouter } from '../../../hooks/useRouter';

// FILTERS ARRAY
export const FILTERS_ARRAY: ISearchFilter[] = [
  {
    key: 'all',
    type: ESearchFilter.ALL,
    translationKey: 'search.searchRecommendations1',
  },
  {
    key: 'onTV',
    type: ESearchFilter.ON_TV,
    translationKey: 'search.searchRecommendations2',
  },
  {
    key: 'onDemand',
    type: ESearchFilter.ON_DEMAND,
    translationKey: 'search.searchRecommendations3',
  },
];

interface ISearchProps {
  isActive: boolean;
  setIsActive: (state: boolean) => void;
}

const Search = React.memo((props: ISearchProps) => {
  const { isActive, setIsActive } = props;

  const location = useLocation();
  const router = useRouter();
  const dispatch = useDispatch();
  const [textSearch, setTextSearch] = useState<string>('');
  const [showOptinRail, setShowOptinRail] = useState<boolean>(false);
  const hasMinChars: boolean = textSearch.length > 2;

  // Set searched text
  const onChangeSearchHandler = (text: string) => {
    setTextSearch(text);
  };

  useSearchAnalytics(isActive);

  // Get Filter Options from Menu
  const menuItem = useSelector((store: StoreState) => Ducks.Menu.Selectors.getMainMenuItem(store, MenuUiArea.SEARCH));
  const assets = useSelector(SearchDuck.Selectors.getResultAssets);
  const noResults = useSelector(SearchDuck.Selectors.getNoResults);
  const searchError = useSelector(SearchDuck.Selectors.getError);
  const isLoadingSearchRequest: boolean = useSelector(SearchDuck.Selectors.isSearchRequestComplete);
  const isLoadingSearchMoreAssets: boolean = useSelector(SearchDuck.Selectors.isSearchMoreAssetsLoading);
  const curUserId: string = useSelector(Ducks.Credentials.Selectors.getUserId);
  const pageIndexToRequest = useSelector(SearchDuck.Selectors.getSearchResultsPageIndex);
  const storedAssets = useSelector(SearchDuck.Selectors.recentAssetsForUser);
  const configs: Configs | undefined = useSelector(Ducks.Dms.Selectors.getDmsConfigs);
  const [filteredStoredAssets, setFilteredStoredAssets] = useState<Asset[]>([]);

  useEffect(() => {
    if (storedAssets && configs) {
      setFilteredStoredAssets(
        EpgUtils.filterInvalidEpgPrograms(storedAssets, configs.epgBackwardDays, configs.epgForwardDays)
      );
    }
  }, [configs, storedAssets]);

  /* *** RECOMENDATIONS *** */
  // Flag to see if recomendations is Active
  const isRecomendationsActive = useSelector(Ducks.Recommendations.Selectors.isFeatureEnabled);
  const isGdprConsent = useSelector(Gdpr.Selectors.hasAgreedToConsent);
  const getActiveFilter = useSelector(SearchDuck.Selectors.getActiveFilter);

  // Get Filters from Menu Duck witch have the type 'externalsearch'
  const filtersRecom: MenuItem[] = useMemo(
    () => menuItem?.children.filter((item) => item.type === 'externalsearch') || [],
    [menuItem?.children]
  );

  // No more results in Recomm
  const noMoreResults = useSelector(SearchDuck.Selectors.noMoreResults);

  // No more assets for this search (Non-Recom)
  const searchCompleted = useSelector(SearchDuck.Selectors.completed);

  useEffect(() => {
    if (isActive) {
      dispatch(SearchDuck.ActionCreators.initRecentAssets());
    }
  }, [dispatch, isActive]);

  useEffect(() => {
    // SPEC: Only dispatch the search if the user inserted at least 3 chars on the imputText
    if (hasMinChars && isActive) {
      // Activate the ALL filter on each new search
      let filter: SearchDuck.ActiveFilter = { filterKey: ESearchFilter.ALL };
      if (isRecomendationsActive) {
        filter = { filterKey: '0', menuItemId: filtersRecom.length ? filtersRecom[0].id : undefined };
      }
      dispatch(SearchDuck.ActionCreators.setActiveFilter(filter, isRecomendationsActive, isGdprConsent));
      // Hide Optin rail on each new search
      setShowOptinRail(false);

      dispatch(SearchDuck.ActionCreators.setQuery(textSearch));
      dispatch(SearchDuck.ActionCreators.searchAssets(Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS));
    }
    // Clear text from variable on each search dropdown toggle
    if (!isActive) {
      setTextSearch('');
    }
  }, [textSearch, dispatch, hasMinChars, isActive, isRecomendationsActive, isGdprConsent, filtersRecom]);

  // ****** RECENT SEARCHES *********
  // Add to Redux store on click
  const storeRecentAsset = (asset: Asset, menuItemId?: number) => {
    const obj = {
      [curUserId]: { assets: [asset] },
    };
    const mainSection = location.pathname.split('/')[1] as MainSection | undefined;
    dispatch(SearchDuck.ActionCreators.addToRecent(curUserId, obj));

    if (mainSection) {
      router.push({ asset, entryPoint: EntryPoint.SEARCH, menuItemId });
      setIsActive(false);
    }
  };

  // Get the "OPTIN" option selected from the Filters
  const optinHandler = (active: boolean) => {
    setShowOptinRail(active);
  };

  // #region INFINIT SCROLL
  const gridRef = useRef(null);
  const isOnViewport = useIsOnViewportObserver(gridRef, {
    root: null,
    rootMargin: '0px',
    threshold: [0.9, 1],
  });

  useEffect(() => {
    if (isOnViewport) {
      if (!isRecomendationsActive || (isRecomendationsActive && !isGdprConsent)) {
        dispatch(SearchDuck.ActionCreators.loadMoreAssets(pageIndexToRequest));
      }
      if (isRecomendationsActive && isGdprConsent) {
        dispatch(SearchDuck.ActionCreators.loadMoreAssets(pageIndexToRequest));
      }
    }
  }, [dispatch, isGdprConsent, isOnViewport, isRecomendationsActive, pageIndexToRequest]);
  // #endregion INFINIT SCROLL

  const endConsentAccept = () => {
    setShowOptinRail(false);
    if (hasMinChars) {
      dispatch(SearchDuck.ActionCreators.setQuery(textSearch));
      dispatch(SearchDuck.ActionCreators.searchAssets(Constants.MAX_ASSETS_PER_RAIL_SEARCH_RESULTS));
    }
  };

  const onErrorDismissed = () => {
    dispatch(SearchDuck.ActionCreators.clearSeachError());
  };

  return (
    <div className={`searchWrapper ${isActive ? 'isActive' : ''}`}>
      {isActive && (
        <SearchBar
          placeholder={translate['search.inputPlaceholder']}
          onChange={(e) => {
            onChangeSearchHandler(e);
          }}
          isActive={isActive}
        />
      )}
      {isActive && hasMinChars && (
        <>
          <div className="searchResultsWrapper">
            <div className="inner-content">
              {FILTERS_ARRAY && filtersRecom && (
                <>
                  <h2 id="Search_resultsTitle">{translate['search.searchresults']}</h2>
                  <SearchFilterList
                    filters={FILTERS_ARRAY}
                    filtersRecom={filtersRecom}
                    isRecomendations={isRecomendationsActive}
                    consent={isGdprConsent}
                    optInCallback={optinHandler}
                  />
                </>
              )}
              <div id="Search_resultsContainer" className="search-results-container">
                {showOptinRail && (
                  <div>
                    <OptInRail
                      railId="optInSearchResults"
                      railName=""
                      railBackgroundImageName={RailBackgroundImageName.RELATED_RECOMM}
                      railDescription={translate['firstTimeUse.recommendations.optIn.empty']}
                      optInClicked
                      continueClicked={endConsentAccept}
                    />
                  </div>
                )}
                {assets && !isLoadingSearchRequest && !noResults ? (
                  <Grid
                    railId="searchResults"
                    menuItemId={getActiveFilter.menuItemId}
                    className="no-background"
                    assets={assets}
                    onCardClick={storeRecentAsset}
                  />
                ) : null}
                <div
                  className={`load-more-infinit-scroll ${
                    !noResults && !noMoreResults && !searchCompleted && !isLoadingSearchRequest ? '' : 'hidden'
                  }`}
                  ref={gridRef}
                />
                {(isLoadingSearchRequest || isLoadingSearchMoreAssets) &&
                (!noResults || !noMoreResults) &&
                !searchCompleted &&
                getActiveFilter.filterKey !== ESearchFilter.OPT_IN ? (
                  <div className="layout-spinner-container load-more">
                    <Spinner isActive />
                  </div>
                ) : null}
                {!isLoadingSearchRequest && noResults && getActiveFilter.filterKey !== ESearchFilter.OPT_IN ? (
                  <div className="search-title" id="Search_message___noResults">
                    {translate['search.noresultsfoundfor']}&nbsp;{`“${textSearch}”`}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}
      {isActive && filteredStoredAssets.length > 0 ? (
        <RailComponent
          railId="recentSearches"
          menuItemId={isRecomendationsActive && filtersRecom.length ? filtersRecom[0].id : undefined}
          className="no-background recent-results-rail"
          railName={translate['search.recentsearches']}
          maxNumberOfAssets={Constants.MAX_ASSETS_PER_RAIL_SEARCH_RECENT}
          assets={filteredStoredAssets}
          onCardClick={storeRecentAsset}
        />
      ) : null}
      <Modal
        title={translate['modal.title.error']}
        message={searchError?.translation}
        buttons={[{ id: 'ok', text: translate.modalButtonOk, onClick: onErrorDismissed }]}
        isShowing={isActive && searchError !== undefined}
        setIsShowing={onErrorDismissed}
      />
    </div>
  );
});

export default Search;
